import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import VoiceUploadForm from './components/VoiceUploadForm';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import { useVoices } from '../../hooks/useApi';
import { Construction, Copy, Check } from 'lucide-react';
import '../../styles/index.css';
import './MyVoices.css';

// Static decorative waveform SVG for voice card header
const WaveformPreview = () => {
  const bars = [4, 8, 5, 11, 7, 9, 5, 12, 6, 8, 4, 10, 7, 5, 11, 8, 6, 10, 4, 7];
  return (
    <div className="mv-wave-area">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${bars.length * 6} 44`}
        preserveAspectRatio="xMidYMid meet"
      >
        {bars.map((h, i) => (
          <rect
            key={i}
            x={i * 6 + 1}
            y={(44 - h * 3.2) / 2}
            width={4}
            height={h * 3.2}
            rx={2}
            fill="currentColor"
            opacity={0.45 + (i % 5) * 0.11}
          />
        ))}
      </svg>
    </div>
  );
};

const GENDER_LABEL = { male: '男', female: '女' };

const MyVoices = () => {
  const [activeTab, setActiveTab] = useState('my-voices');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedKey, setCopiedKey] = useState(null);
  const [toast, setToast] = useState('');
  const copyTimerRef = useRef(null);
  const toastTimerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from;
  const fromTab = location.state?.fromTab;

  const myVoicesData = useVoices('my', currentPage, searchQuery);
  const publicVoicesData = useVoices('public', currentPage, searchQuery);

  const getCurrentData = () => {
    if (activeTab === 'my-voices') return myVoicesData;
    if (activeTab === 'public') return publicVoicesData;
    return { voices: [], loading: false, error: null, totalPages: 0, total: 0 };
  };

  const { voices, loading, error, totalPages, total, refetch } = getCurrentData();

  // Client-side filter as fallback (in case backend ignores search param)
  const filteredVoices = useMemo(() => {
    if (!searchQuery) return voices;
    const q = searchQuery.toLowerCase();
    return voices.filter(v => v.name.toLowerCase().includes(q));
  }, [voices, searchQuery]);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) setCurrentPage(1);
    else if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '我的声音', path: null },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(''), 2500);
  };

  const handleSelectVoice = (voice) => {
    localStorage.setItem('selectedVoice', JSON.stringify(voice));
    if (returnTo) {
      navigate(returnTo, { replace: true, state: fromTab ? { tab: fromTab } : undefined });
    } else {
      showToast(`已选择声音：${voice.name}`);
    }
  };

  const handleDeleteVoice = async (voice) => {
    if (activeTab !== 'my-voices') return;
    if (!window.confirm(`确认删除声音「${voice.name}」吗？`)) return;
    try {
      await myVoicesData.deleteVoice(voice.id);
    } catch (err) {
      alert(err?.message || '删除失败');
    }
  };

  const handleCopyKey = useCallback(async (key) => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      setCopiedKey(key);
      copyTimerRef.current = setTimeout(() => setCopiedKey(null), 2000);
    } catch { /* ignore */ }
  }, []);

  const handleUploadSuccess = () => {
    setActiveTab('my-voices');
    myVoicesData.refetch();
  };

  const handlePageChange = (page) => {
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;
    setCurrentPage(page);
  };

  const highlightText = (text, keyword) => {
    if (!keyword || !text) return text;
    const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="mv-name-highlight">{text.slice(idx, idx + keyword.length)}</mark>
        {text.slice(idx + keyword.length)}
      </>
    );
  };

  return (
    <div className="my-voices-page">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header */}
      <div className="mv-page-header">
        <h1 className="mv-page-title">
          {activeTab === 'upload' ? '克隆声音' : '我的声音'}
        </h1>
        {activeTab !== 'upload' && (
          <button className="mv-create-btn" onClick={() => handleTabClick('upload')}>
            + 克隆声音
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mv-tabs">
        <button
          className={`mv-tab${activeTab === 'my-voices' ? ' active' : ''}`}
          onClick={() => handleTabClick('my-voices')}
        >
          我的声音
        </button>
        <button
          className={`mv-tab${activeTab === 'public' ? ' active' : ''}`}
          onClick={() => handleTabClick('public')}
        >
          公共库
        </button>
        <button
          className={`mv-tab${activeTab === 'upload' ? ' active' : ''}`}
          onClick={() => handleTabClick('upload')}
        >
          克隆声音
        </button>
      </div>

      {/* Toolbar — always rendered for consistent spacing */}
      <div className="mv-toolbar">
        {activeTab !== 'upload' && (
          <>
            <input
              className="mv-search-box"
              placeholder={activeTab === 'my-voices' ? '搜索我的声音...' : '搜索公共声音...'}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {activeTab === 'my-voices' && total > 0 && <span className="mv-total-count">共 {total} 个声音</span>}
          </>
        )}
      </div>

      {/* Content */}
      {activeTab === 'upload' ? (
        <VoiceUploadForm onSuccess={handleUploadSuccess} />
      ) : activeTab === 'public' ? (
        <div className="mv-wip-state">
          <div className="mv-wip-icon"><Construction size={28} /></div>
          <p className="mv-wip-title">公共库正在建设中</p>
          <p className="mv-wip-desc">我们正在精心整理公共声音资源，敬请期待，即将上线</p>
        </div>
      ) : (
        <>
          {loading ? (
            <div className="mv-loading">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="mv-error">
              <p>加载失败: {error}</p>
              <button onClick={() => refetch?.()}>重试</button>
            </div>
          ) : filteredVoices.length === 0 ? (
            <div className="mv-empty">
              {searchQuery ? '没有找到匹配的声音' : '点击「克隆声音」开始上传您的第一个声音'}
            </div>
          ) : (
            <>
              <div className="mv-cards-grid">
                {filteredVoices.map((voice) => {
                  const displayKey = voice.key || voice.id || '';
                  const isCopied = copiedKey === displayKey;
                  const gender = voice.gender || 'unknown';
                  return (
                    <div key={voice.id} className="mv-voice-card">
                      <div className={`mv-wave-wrap mv-wave-${gender}`}>
                        <WaveformPreview />
                        {GENDER_LABEL[gender] && (
                          <span className="mv-gender-badge">{GENDER_LABEL[gender]}</span>
                        )}
                      </div>
                      <div className="mv-voice-name">{highlightText(voice.name, searchQuery)}</div>
                      {displayKey && (
                        <button
                          className={`mv-key-copy${isCopied ? ' copied' : ''}`}
                          onClick={() => handleCopyKey(displayKey)}
                          title="点击复制声音Key"
                        >
                          <span className="mv-key-text">{displayKey.substring(0, 16)}...</span>
                          {isCopied ? <Check size={12} /> : <Copy size={12} />}
                          {isCopied && <span className="mv-key-tooltip">声音Key已复制</span>}
                        </button>
                      )}
                      <div className="mv-card-actions">
                        <button className="mv-select-btn" onClick={() => handleSelectVoice(voice)}>
                          选择
                        </button>
                        {activeTab === 'my-voices' && (
                          <button className="mv-delete-btn" onClick={() => handleDeleteVoice(voice)}>
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mv-pagination">
                  <button
                    className="mv-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    ←
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 7) pageNum = i + 1;
                    else if (currentPage <= 4) pageNum = i + 1;
                    else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                    else pageNum = currentPage - 3 + i;
                    return (
                      <button
                        key={pageNum}
                        className={`mv-page-num${currentPage === pageNum ? ' active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="mv-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div className="mv-toast">
          <Check size={14} />
          {toast}
        </div>
      )}
    </div>
  );
};

export default MyVoices;
